import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { useRegion } from '@/contexts/RegionContext';
import { Turnstile } from '@/components/ui/Turnstile';

export const COUNTRIES_LIST = [
  'South Africa',
  'United Kingdom',
  'United States',
  'Australia',
  'Canada',
  'New Zealand',
  'Ireland',
  'Singapore',
  'United Arab Emirates',
  'Germany',
  'Netherlands',
  'France',
  'Switzerland',
  'Belgium',
  'Spain',
  'Italy',
  'Portugal',
  'Sweden',
  'Norway',
  'Denmark',
  'Finland',
  'Austria',
  'Saudi Arabia',
  'Qatar',
  'Hong Kong',
  'Japan',
  'Brazil',
  'Mexico',
  'India',
  'Nigeria',
  'Kenya',
  'Ghana',
  'Zimbabwe',
  'Namibia',
  'Botswana',
  'Other'
];

export const LANGUAGE_OPTIONS = [
  'English',
  'Afrikaans',
  'isiZulu',
  'isiXhosa',
  'Spanish',
  'French'
];

export interface PractitionerIntroFormData {
  fullName: string;
  email: string;
  phone: string;
  country: string;
  lookingFor: string;
  preferredLanguage: string;
  comments: string;
  consent: boolean;
  website: string; // Honeypot field
}

export function PractitionerIntroForm() {
  const { t } = useTranslation();
  const { regionCode } = useRegion();

  const [formData, setFormData] = useState<PractitionerIntroFormData>({
    fullName: '',
    email: '',
    phone: '',
    country: '',
    lookingFor: '',
    preferredLanguage: '',
    comments: '',
    consent: false,
    website: '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [resetKey, setResetKey] = useState(0);

  // Set default country based on detected region
  useEffect(() => {
    if (!formData.country) {
      const code = (regionCode || '').toLowerCase();
      if (code === 'za') {
        setFormData((prev) => ({ ...prev, country: 'South Africa' }));
      } else if (code === 'gb' || code === 'uk') {
        setFormData((prev) => ({ ...prev, country: 'United Kingdom' }));
      } else if (code === 'us') {
        setFormData((prev) => ({ ...prev, country: 'United States' }));
      } else if (code === 'au') {
        setFormData((prev) => ({ ...prev, country: 'Australia' }));
      } else if (code === 'ca') {
        setFormData((prev) => ({ ...prev, country: 'Canada' }));
      } else if (code === 'nz') {
        setFormData((prev) => ({ ...prev, country: 'New Zealand' }));
      } else {
        setFormData((prev) => ({ ...prev, country: 'United Kingdom' }));
      }
    }
  }, [regionCode]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validations
    if (!formData.fullName.trim()) {
      setError(t('practitionerIntro.errorName', 'Please enter your full name.'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      setError(t('practitionerIntro.errorEmail', 'Please enter a valid email address.'));
      return;
    }

    if (!formData.country) {
      setError(t('practitionerIntro.errorCountry', 'Please select your country.'));
      return;
    }

    if (!formData.lookingFor) {
      setError(t('practitionerIntro.errorLookingFor', 'Please tell us what you are looking for.'));
      return;
    }

    if (!formData.consent) {
      setError(t('practitionerIntro.errorConsent', 'Please accept the privacy policy agreement.'));
      return;
    }

    if (!turnstileToken) {
      setError(t('practitionerIntro.errorTurnstile', 'Please complete the Turnstile verification.'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('/api/practitioner-intro', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          country: formData.country,
          lookingFor: formData.lookingFor,
          preferredLanguage: formData.preferredLanguage,
          comments: formData.comments,
          consent: formData.consent,
          website: formData.website, // Honeypot
          turnstileToken,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || t('practitionerIntro.errorMessage', 'Failed to submit request. Please try again or contact us directly.'));
      }

      // Success logic: push dataLayer event & show thank-you view
      if (typeof window !== 'undefined') {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: 'generate_lead',
          lead_type: 'practitioner_intro',
        });
      }

      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || t('practitionerIntro.errorMessage', 'Something went wrong submitting your request. Please try again or email office@enneainternational.com.'));
      setTurnstileToken('');
      setResetKey(prev => prev + 1);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div 
        id="practitioner-intro-success"
        className="bg-white border border-ground-deep p-8 md:p-12 shadow-sm text-center"
      >
        <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <p className="text-ink text-lg md:text-xl font-serif max-w-xl mx-auto leading-relaxed">
          {t('practitionerIntro.successMessage', 'Thank you. We will be in touch within two working days to arrange your introduction.')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ground-deep p-8 md:p-10 shadow-sm">
      {error && (
        <div className="p-4 mb-6 bg-rose-50 border border-rose-200 text-rose-800 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 text-rose-600 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold">{t('practitionerIntro.errorTitle', 'Submission Error')}</p>
            <p className="text-xs mt-0.5">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6" noValidate>
        {/* Hidden Honeypot Field */}
        <div className="hidden" aria-hidden="true">
          <label htmlFor="hp-website">Website</label>
          <input
            id="hp-website"
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={formData.website}
            onChange={handleChange}
          />
        </div>

        {/* Full Name & Email */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="prac-fullName" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
              {t('practitionerIntro.fullNameLabel', 'Full Name')} <span className="text-rose-600">*</span>
            </label>
            <input
              id="prac-fullName"
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              disabled={loading}
              required
              placeholder={t('practitionerIntro.fullNamePlaceholder', 'e.g. Sarah Jenkins')}
              className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50"
            />
          </div>

          <div>
            <label htmlFor="prac-email" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
              {t('practitionerIntro.emailLabel', 'Email Address')} <span className="text-rose-600">*</span>
            </label>
            <input
              id="prac-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              required
              placeholder={t('practitionerIntro.emailPlaceholder', 'sarah@example.com')}
              className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50"
            />
          </div>
        </div>

        {/* Phone & Country */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="prac-phone" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
              {t('practitionerIntro.phoneLabel', 'Phone Number')} <span className="text-ink/40 font-normal">(Optional)</span>
            </label>
            <input
              id="prac-phone"
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              disabled={loading}
              placeholder={t('practitionerIntro.phonePlaceholder', '+1 555 123 4567')}
              className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50"
            />
          </div>

          <div>
            <label htmlFor="prac-country" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
              {t('practitionerIntro.countryLabel', 'Country')} <span className="text-rose-600">*</span>
            </label>
            <select
              id="prac-country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              disabled={loading}
              required
              className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50 appearance-none cursor-pointer"
            >
              <option value="" disabled>
                {t('practitionerIntro.countrySelectPrompt', 'Select your country')}
              </option>
              {COUNTRIES_LIST.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* What are you looking for? & Preferred language */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="prac-lookingFor" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
              {t('practitionerIntro.lookingForLabel', 'What are you looking for?')} <span className="text-rose-600">*</span>
            </label>
            <select
              id="prac-lookingFor"
              name="lookingFor"
              value={formData.lookingFor}
              onChange={handleChange}
              disabled={loading}
              required
              className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50 appearance-none cursor-pointer"
            >
              <option value="" disabled>
                {t('practitionerIntro.lookingForSelectPrompt', 'Select an option...')}
              </option>
              <option value="One-to-one coaching and personal debrief">
                {t('practitionerIntro.optionCoaching', 'One-to-one coaching and personal debrief')}
              </option>
              <option value="Team development or team workshop">
                {t('practitionerIntro.optionTeam', 'Team development or team workshop')}
              </option>
              <option value="Leadership or executive coaching">
                {t('practitionerIntro.optionLeadership', 'Leadership or executive coaching')}
              </option>
              <option value="Not sure yet">
                {t('practitionerIntro.optionNotSure', 'Not sure yet')}
              </option>
            </select>
          </div>

          <div>
            <label htmlFor="prac-preferredLanguage" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
              {t('practitionerIntro.languageLabel', 'Preferred Language')} <span className="text-ink/40 font-normal">(Optional)</span>
            </label>
            <select
              id="prac-preferredLanguage"
              name="preferredLanguage"
              value={formData.preferredLanguage}
              onChange={handleChange}
              disabled={loading}
              className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50 appearance-none cursor-pointer"
            >
              <option value="">
                {t('practitionerIntro.languageSelectPrompt', 'Select language...')}
              </option>
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Anything else we should know? */}
        <div>
          <label htmlFor="prac-comments" className="block text-xs font-bold text-ink uppercase tracking-wider mb-2">
            {t('practitionerIntro.commentsLabel', 'Anything else we should know?')} <span className="text-ink/40 font-normal">(Optional, max 1000 characters)</span>
          </label>
          <textarea
            id="prac-comments"
            name="comments"
            rows={4}
            maxLength={1000}
            value={formData.comments}
            onChange={handleChange}
            disabled={loading}
            placeholder={t('practitionerIntro.commentsPlaceholder', 'Tell us a bit about your goals, timing, or team size...')}
            className="w-full bg-ground border border-ground-deep p-4 text-ink text-sm outline-none focus:ring-2 focus:ring-rule-3 transition-colors disabled:opacity-50 resize-y"
          />
          <div className="text-right text-[11px] text-ink/40 mt-1">
            {formData.comments.length} / 1000
          </div>
        </div>

        {/* Consent Checkbox */}
        <div className="pt-2">
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              id="prac-consent"
              type="checkbox"
              name="consent"
              checked={formData.consent}
              onChange={handleChange}
              disabled={loading}
              required
              className="w-4 h-4 mt-1 accent-rule-3 cursor-pointer border-ground-deep rounded"
            />
            <span className="text-xs text-ink/80 leading-relaxed">
              {t('practitionerIntro.consentText', 'I agree that FIVE LENS may use these details to arrange an introduction, as set out in the')}{' '}
              <Link to="/privacy-policy" target="_blank" className="underline hover:text-rule-3 font-medium">
                {t('practitionerIntro.privacyPolicyLink', 'Privacy Policy')}
              </Link>. <span className="text-rose-600">*</span>
            </span>
          </label>
        </div>

        <Turnstile onVerify={(token) => setTurnstileToken(token)} resetKey={resetKey} />

        {/* Submit Button */}
        <div>
          <button
            id="prac-submit-btn"
            type="submit"
            disabled={loading || !turnstileToken}
            className="w-full sm:w-auto bg-ink text-white px-8 py-4 uppercase tracking-widest text-xs font-bold hover:bg-ink/70 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{t('practitionerIntro.submitting', 'Submitting Request...')}</span>
              </>
            ) : (
              t('practitionerIntro.submitBtn', 'Request an introduction')
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
